// Freehand export dialog — feeds the committed freehand panels through the
// existing SVG/DXF export pipeline. Freehand panels have their own vertex
// list per panel (no shared edges), so edges are naturally treated as
// boundaries and each panel gets its own stitch holes around its perimeter.

import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { buildPanelSheet } from '@/lib/panelSheet';
import { renderDXF } from '@/lib/panelSheetDXF';
import { toSTL, toOBJ, toJSON, downloadText, downloadDataURL } from '@/lib/exporters';
import { useFreehand } from '@/lib/freehandStore';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { FileDown, Image as ImageIcon, Braces, Scissors, ChevronDown, Lock } from 'lucide-react';
import PanelSheetPreview from '@/components/studio/PanelSheetPreview';

export default function FreehandExportDialog({ open, onOpenChange, captureFn, onNeedsUpgrade }) {
  const [name, setName] = useState('My Freehand Ball');
  const [advanced, setAdvanced] = useState(false);
  const [ballDiameter, setBallDiameter] = useState(213.2);
  const [seamAllowance, setSeamAllowance] = useState(2.5);
  const [holeDiameter, setHoleDiameter] = useState(1);
  const [holeSpacing, setHoleSpacing] = useState(4);
  const [holeStyle, setHoleStyle] = useState('round');
  const [slitLength, setSlitLength] = useState(1);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const panels = useFreehand((s) => s.panels);
  const getMesh = useFreehand((s) => s.getMesh);
  const getColors = useFreehand((s) => s.getColors);
  const spherized = useFreehand((s) => s.spherized);
  const { isFreehandPro: isPro } = useAuth();

  const guardPro = () => {
    if (isPro) return true;
    onNeedsUpgrade && onNeedsUpgrade();
    return false;
  };
  const guardPanels = () => {
    if (panels.length === 0) {
      toast.error('Draw at least one panel before exporting');
      return false;
    }
    return true;
  };

  const handleSTL = () => {
    if (!guardPro() || !guardPanels()) return;
    const { vertices, faces } = getMesh();
    downloadText(`${slug(name)}.stl`, toSTL(vertices, faces, 1.25), 'model/stl');
    toast.success('STL exported');
  };
  const handleOBJ = () => {
    if (!guardPro() || !guardPanels()) return;
    const { vertices, faces } = getMesh();
    downloadText(`${slug(name)}.obj`, toOBJ(vertices, faces, 1.25), 'text/plain');
    toast.success('OBJ exported');
  };
  const handlePNG = () => {
    if (!guardPro()) return;
    if (!captureFn) return toast.error('Canvas not ready');
    downloadDataURL(`${slug(name)}.png`, captureFn());
    toast.success('PNG exported');
  };
  const handleJSON = () => {
    if (!guardPro() || !guardPanels()) return;
    const design = {
      name,
      template: 'freehand',
      panels: panels.map((p) => ({ id: p.id, color: p.color, points: p.points })),
    };
    downloadText(`${slug(name)}.json`, toJSON(design), 'application/json');
    toast.success('JSON exported');
  };
  const buildOpts = (overrides) => ({
    ballDiameterMM: Number(ballDiameter),
    seamAllowanceMM: Number(seamAllowance),
    holeDiameterMM: Number(holeDiameter),
    holeSpacingMM: Number(holeSpacing),
    holeStyle,
    slitLengthMM: Number(slitLength),
    foamInsetMM: 3,
    sheetWidthMM: 300,
    spherized,
    smoothVertices: [],
    faceHoles: getMesh().faceHoles || [],
    ...(overrides || {}),
  });

  const handleSVG = (overrides) => {
    if (!guardPro() || !guardPanels()) return;
    try {
      const { vertices, faces } = getMesh();
      const opts = buildOpts(overrides);
      const { svg, panels: panelCount, sheetHeightMM } = buildPanelSheet(vertices, faces, opts);
      const suffix = overrides?.presetSlug ? `-${overrides.presetSlug}` : '-panels';
      setPreviewData({
        svg,
        filename: `${slug(name)}${suffix}.svg`,
        meta: { panels: panelCount, sheetHeightMM, ...opts },
      });
      setPreviewOpen(true);
    } catch (e) {
      console.error(e);
      toast.error('Panel sheet generation failed');
    }
  };
  const handleDXF = (overrides) => {
    if (!guardPro() || !guardPanels()) return;
    try {
      const { vertices, faces } = getMesh();
      const opts = buildOpts(overrides);
      const { laid, renderOpts } = buildPanelSheet(vertices, faces, opts);
      const dxf = renderDXF(laid, renderOpts);
      const suffix = overrides?.presetSlug ? `-${overrides.presetSlug}` : '-panels';
      downloadText(`${slug(name)}${suffix}.dxf`, dxf, 'application/dxf');
      toast.success('DXF exported');
    } catch (e) {
      console.error(e);
      toast.error('DXF export failed');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="freehand-export-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg tracking-tight flex items-center gap-2">
              Export freehand design
              {!isPro && (
                <span className="text-[10px] font-semibold uppercase tracking-widest bg-slate-900 text-white rounded-full px-2 py-0.5">
                  Pro · $9
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {isPro
                ? 'Turn your hand-drawn panels into printable patterns exactly like the Studio.'
                : 'Every export below unlocks with the one-time $9 Pro upgrade.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Design name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="freehand-export-name-input"
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <ExportBtn testId="freehand-btn-export-stl" onClick={handleSTL} icon={<FileDown className="w-4 h-4" />} locked={!isPro}>
                STL <span className="block text-[10px] text-slate-500">3D printable</span>
              </ExportBtn>
              <ExportBtn testId="freehand-btn-export-obj" onClick={handleOBJ} icon={<FileDown className="w-4 h-4" />} locked={!isPro}>
                OBJ <span className="block text-[10px] text-slate-500">3D mesh</span>
              </ExportBtn>
              <ExportBtn testId="freehand-btn-export-png" onClick={handlePNG} icon={<ImageIcon className="w-4 h-4" />} locked={!isPro}>
                PNG <span className="block text-[10px] text-slate-500">Studio snapshot</span>
              </ExportBtn>
              <ExportBtn testId="freehand-btn-export-json" onClick={handleJSON} icon={<Braces className="w-4 h-4" />} locked={!isPro}>
                JSON <span className="block text-[10px] text-slate-500">Design data</span>
              </ExportBtn>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2">
              <div className="flex gap-2">
                <button
                  onClick={() => handleSVG()}
                  data-testid="freehand-btn-export-svg"
                  className="flex-1 rounded-lg bg-white border border-slate-300 hover:border-slate-400 hover:bg-slate-50 p-3 text-left transition-colors flex items-start gap-3"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#111827] text-white flex items-center justify-center shrink-0">
                    {isPro ? <Scissors className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-1">
                      Freehand panels (SVG)
                      {!isPro && <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Pro</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                      Flatten every freehand panel · Ø{ballDiameter}mm ball · +{seamAllowance}mm seam ·
                      Ø{holeDiameter}mm holes every {holeSpacing}mm · yellow foam-liner cut (3mm inset)
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => handleDXF()}
                  data-testid="freehand-btn-export-dxf"
                  title="Download DXF"
                  className="shrink-0 w-16 rounded-lg bg-white border border-slate-300 hover:border-slate-400 hover:bg-slate-50 p-2 flex flex-col items-center justify-center gap-1 text-slate-900"
                >
                  {isPro ? <FileDown className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  <span className="text-[11px] font-semibold tracking-wide">DXF</span>
                </button>
              </div>
              <button
                onClick={() => setAdvanced((v) => !v)}
                data-testid="freehand-btn-advanced-toggle"
                className="mt-2 text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1"
              >
                <ChevronDown className={`w-3 h-3 transition-transform ${advanced ? 'rotate-180' : ''}`} />
                {advanced ? 'Hide' : 'Adjust'} regulation dimensions
              </button>
              {advanced && (
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <NumField label="Ball Ø (mm)" value={ballDiameter} onChange={setBallDiameter} testId="freehand-input-ball-diameter" step="0.1" />
                    <NumField label="Seam allowance (mm)" value={seamAllowance} onChange={setSeamAllowance} testId="freehand-input-seam-allowance" step="0.1" />
                    {holeStyle === 'round' ? (
                      <NumField label="Hole Ø (mm)" value={holeDiameter} onChange={setHoleDiameter} testId="freehand-input-hole-diameter" step="0.1" />
                    ) : (
                      <NumField label="Slit length (mm)" value={slitLength} onChange={setSlitLength} testId="freehand-input-slit-length" step="0.1" />
                    )}
                    <NumField label="Hole spacing (mm)" value={holeSpacing} onChange={setHoleSpacing} testId="freehand-input-hole-spacing" step="0.1" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Hole style</div>
                    <div className="grid grid-cols-2 gap-1.5" data-testid="freehand-hole-style-group">
                      <button
                        type="button"
                        onClick={() => setHoleStyle('round')}
                        data-testid="freehand-hole-style-round"
                        className={`text-[11px] rounded-lg px-2 py-1.5 border transition-colors ${holeStyle === 'round' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'}`}
                      >
                        Round
                        <div className={`text-[10px] mt-0.5 ${holeStyle === 'round' ? 'text-slate-300' : 'text-slate-500'}`}>Drill-friendly circles</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setHoleStyle('slit')}
                        data-testid="freehand-hole-style-slit"
                        className={`text-[11px] rounded-lg px-2 py-1.5 border transition-colors ${holeStyle === 'slit' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'}`}
                      >
                        Awl slits (45°)
                        <div className={`text-[10px] mt-0.5 ${holeStyle === 'slit' ? 'text-slate-300' : 'text-slate-500'}`}>Hand-pricked saddler's seam</div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="text-[11px] text-slate-500 leading-snug rounded-xl bg-blue-50 border border-blue-100 p-3">
              Freehand panels aren't tessellated as a shared ball, so exported holes are calculated per-panel around each perimeter — great for hand-stitched or laser-cut prototypes.
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <PanelSheetPreview
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        svg={previewData?.svg}
        meta={previewData?.meta}
        filename={previewData?.filename || 'freehand-panels.svg'}
      />
    </>
  );
}

function NumField({ label, value, onChange, testId, step = '1' }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-slate-500 mb-1">{label}</span>
      <Input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="rounded-lg h-9 text-sm bg-white"
      />
    </label>
  );
}
function ExportBtn({ children, onClick, icon, testId, locked }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative rounded-xl border p-3 text-left transition-colors ${
        locked ? 'border-slate-200 bg-slate-50 hover:border-slate-300' : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
        {locked ? <Lock className="w-4 h-4 text-slate-500" /> : icon}
        {children}
      </div>
    </button>
  );
}
function slug(s) {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'design';
}
